import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { Upload } from 'lucide-react';
import { cn } from '@common/utils/cn';

interface ExplorerDropzoneProps {
  /** Identifica a pasta-alvo para o copy do overlay (undefined = raiz). */
  folderName?: string;
  /** Bloqueia o DnD (ex.: sem permissão de escrita, página de busca). */
  disabled?: boolean;
  onDrop: (files: globalThis.File[]) => void;
  children: ReactNode;
}

/**
 * Envolve uma área da UI permitindo soltar arquivos do SO para iniciar upload.
 *
 * - `noClick`/`noKeyboard`: o wrapper **não** abre file picker — apenas captura
 *   drop. O click manual continua sendo do botão "Enviar" do header.
 * - Overlay aparece só quando `isDragActive`, com `pointer-events-none` para
 *   não bloquear o `drop` no wrapper. Detecção de drag-enter/leave em
 *   containers aninhados é gerenciada pelo próprio `react-dropzone`.
 */
export function ExplorerDropzone({
  folderName,
  disabled,
  onDrop,
  children,
}: ExplorerDropzoneProps) {
  const { t } = useTranslation('files');

  const { getRootProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: true,
    disabled,
    onDrop: (accepted) => {
      if (accepted.length > 0) onDrop(accepted);
    },
  });

  return (
    <div {...getRootProps()} className="relative min-h-[400px]">
      {children}
      {isDragActive && !disabled && (
        <div
          role="status"
          aria-live="polite"
          className={cn(
            'pointer-events-none absolute inset-0 z-10 flex items-center justify-center',
            'rounded-lg border-2 border-dashed border-brand-primary bg-brand-primary-subtle/90',
          )}
        >
          <div className="flex flex-col items-center gap-2 px-6 py-4 text-center">
            <Upload
              className="w-10 h-10 text-brand-primary"
              strokeWidth={1.5}
              aria-hidden="true"
            />
            <p className="text-base font-medium text-brand-primary">
              {folderName
                ? t('dropOverlayHere')
                : t('dropOverlayRoot')}
            </p>
            <p className="text-sm text-foreground-muted">
              {t('allowedFormats')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
